import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { RecentSearches } from '@theme/recent-searches';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * Turns a product URL's handle into a readable title, e.g. "/products/family-pork-pie" ->
 * "Family Pork Pie". Used as a fallback when a product card's title can't be read from its
 * markup — the card's block layout is merchant-configurable, so scraping it is inherently
 * fragile, while the handle is always present in the link the card already provides.
 * @param {string} url - A product URL, with or without a leading origin or trailing query string.
 * @returns {string}
 */
function titleFromHandle(url) {
  const path = url.split('?')[0] ?? '';
  const handle = path.split('/').filter(Boolean).pop() ?? '';

  return handle
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The scrollable content wrapper holding both state containers.
 * @property {HTMLElement} resultsContainer - The typed-state container that search results morph into.
 * @property {HTMLElement} emptyStateContainer - The merchandised empty state, permanently in the DOM.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'resultsContainer', 'emptyStateContainer'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      // this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
      // predicitive search was being overlapped by view all button
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    // TODO: Could we put a ref directly on the results child
    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Clicking through to a result counts as a search — record it before navigation starts.
    if (target.closest('[data-search-results] a')) {
      RecentSearches.addTerm(this.refs.searchInput.value);
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * Bound to the form's submit, so it covers the arrow button in the search field and any other
   * route to submission. `addTerm` ignores blank and whitespace-only values, and dedupes, so
   * overlapping with the Enter path is harmless.
   */
  recordSearchTerm = () => {
    // Rewrite the field to the corrected spelling before the form navigates, so the results page
    // gets the same query the dropdown was showing. Without this the correction only ever
    // applied to the predictive fetch: typing "wagu" showed wagyu results, then submitting sent
    // the raw "wagu" to /search and returned nothing.
    this.#applyCorrectionToInput();
    RecentSearches.addTerm(this.refs.searchInput.value);
  };

  /**
   * Replaces the input's value with its corrected spelling, if one is configured and the shopper
   * has not explicitly asked for their original term. Returns the value now in the field.
   * @returns {string} The term that will actually be searched.
   */
  #applyCorrectionToInput() {
    const typed = this.refs.searchInput.value.trim();
    if (this.#bypassCorrectionFor === typed.toLowerCase()) return typed;

    const corrected = this.#correctionFor(typed);
    if (corrected) this.refs.searchInput.value = corrected;

    return corrected ?? typed;
  }

  expandSearch = () => {
    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          RecentSearches.addTerm(this.refs.searchInput.value);
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          RecentSearches.addTerm(this.refs.searchInput.value);
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // Same correction as the submit path — this branch builds its own URL rather than
          // submitting the form, so it needs applying here too.
          const query = this.#applyCorrectionToInput();
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', query);
          // Same scoping as the form's hidden input, or this lands unscoped and gets redirected.
          searchUrl.searchParams.set('type', 'product');
          RecentSearches.addTerm(query);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Merchant-configured misspelling map, parsed once from `data-spelling-corrections`.
   *
   * Corrections are applied here rather than in Search & Discovery because the predictive search
   * API has no correction hook — the query has to be fixed before it reaches Shopify.
   *
   * @type {Map<string, string> | null}
   */
  #corrections = null;

  get #spellingCorrections() {
    if (this.#corrections) return this.#corrections;

    this.#corrections = new Map();

    for (const line of (this.dataset.spellingCorrections || '').split('\n')) {
      const [typed, corrected] = line.split('=');
      if (!typed || !corrected) continue;

      const from = typed.trim().toLowerCase();
      const to = corrected.trim();

      // Guard against a merchant mapping a term to itself, which would loop the notice on.
      if (from && to && from !== to.toLowerCase()) this.#corrections.set(from, to);
    }

    return this.#corrections;
  }

  /**
   * @param {string} term - The term the shopper typed.
   * @returns {string | null} The corrected term, or null if no correction applies.
   */
  #correctionFor(term) {
    return this.#spellingCorrections.get(term.trim().toLowerCase()) ?? null;
  }

  /**
   * Set when the shopper clicks "search instead for …", so their original spelling is honoured
   * rather than silently re-corrected on the next keystroke-free search.
   * @type {string | null}
   */
  #bypassCorrectionFor = null;

  /**
   * Renders the "Showing results for X — search instead for Y" notice above the results.
   * Called after morph, because morph replaces everything in the results container.
   * @param {string} corrected - The term actually searched.
   * @param {string} original - What the shopper typed.
   */
  #renderCorrectionNotice(corrected, original) {
    const { resultsContainer } = this.refs;

    const notice = document.createElement('p');
    notice.className = 'predictive-search__correction';

    const showing = document.createElement('span');
    showing.textContent = Theme.translations?.search_showing_results_for
      ? Theme.translations.search_showing_results_for.replace('{{ term }}', corrected)
      : `Showing results for “${corrected}”`;

    const instead = document.createElement('button');
    instead.type = 'button';
    instead.className = 'predictive-search__correction-revert button button-unstyled';
    instead.textContent = Theme.translations?.search_instead_for
      ? Theme.translations.search_instead_for.replace('{{ term }}', original)
      : `Search instead for “${original}”`;

    instead.addEventListener('click', () => {
      this.#bypassCorrectionFor = original.trim().toLowerCase();
      this.refs.searchInput.value = original;
      this.#getSearchResults(original);
    });

    notice.append(showing, ' ', instead);
    resultsContainer.prepend(notice);
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const typed = searchTerm.trim();
    const bypassed = this.#bypassCorrectionFor === typed.toLowerCase();
    const corrected = bypassed ? null : this.#correctionFor(typed);
    const query = corrected ?? typed;

    // Clear the bypass once the shopper types something else, so corrections resume.
    if (!bypassed) this.#bypassCorrectionFor = null;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '10');

    const { resultsContainer } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(resultsContainer, resultsMarkup);

        // Carousels created after DOMContentLoaded miss the sitewide bootstrap in theme.liquid
        // that marks slideshow-component .loaded (lifting the CSS guard that hides every slide
        // but the first until JS is ready). Morphed-in results need the same signal, or every
        // typed-state carousel permanently shows just one item regardless of match count.
        resultsContainer
          .querySelectorAll('slideshow-component')
          .forEach((slider) => slider.classList.add('loaded'));

        this.dataset.searchState = resultsContainer.querySelector('[data-no-results]') ? 'no-results' : 'results';

        if (corrected) this.#renderCorrectionNotice(corrected, typed);

        this.#resetScrollPositions();

        // Everything provisional that a product-code match would replace is suppressed for the
        // duration of the lookup — before the browser paints this frame — and a skeleton stands
        // in for it. That means the "nothing matched" message AND the products block's
        // fallback_collection substitutes.
        //
        // Hiding only the message (which is all this did) still flashed Chocolate Offers under a
        // "Suggested Products" heading for the length of a full results-page fetch, then swapped
        // it for the real match. Hiding the fallback without the skeleton just trades that for a
        // gap. Both are restored if the lookup comes up empty.
        const noResultsMessage = resultsContainer.querySelector('[data-no-results]');
        const hideMessage = noResultsMessage instanceof HTMLElement ? noResultsMessage : null;

        // Covers both of the old branches: the lookup is worth running whenever the panel has no
        // genuine product matches, whether that left a "nothing matched" message (no results at
        // all) or not (matched a collection or page, but no product).
        const willTrySku = Boolean(query.trim()) && !this.#hasRealProducts();

        if (!willTrySku) {
          if (hideMessage) hideMessage.hidden = false;
          return;
        }

        if (hideMessage) hideMessage.hidden = true;
        this.#showSkuLoading();

        this.#trySkuFallback(query, abortController).then((found) => {
          if (abortController.signal.aborted) return;

          this.#clearSkuLoading(!found);
          if (!found && hideMessage) hideMessage.hidden = false;
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Hides the products block's fallback_collection substitutes and stands a skeleton in their
   * place while #trySkuFallback runs. Hidden rather than removed so it can be restored verbatim
   * if the lookup finds nothing — re-rendering it would mean another section request.
   */
  #showSkuLoading() {
    const inner = this.refs.resultsContainer.querySelector('[data-search-results]');
    if (!inner) return;

    inner.querySelector('[data-sku-loading]')?.remove();

    const fallback = inner.querySelector('[data-fallback-products]');

    // Reuse the merchant's own heading so the skeleton, the fallback and the eventual match all
    // sit under the same title and nothing appears to rename itself mid-search.
    const headingText = fallback?.querySelector('.predictive-search-results__title')?.textContent?.trim();
    const skeleton = this.#buildSkuSkeleton(headingText);

    if (fallback instanceof HTMLElement) {
      fallback.hidden = true;
      fallback.before(skeleton);
      return;
    }

    // Same placement logic as #renderSkuFallback, so the skeleton occupies the exact slot the
    // real carousel will land in and nothing shifts when they swap.
    const queriesSection = inner.querySelector('.search-drawer__term-list')?.closest('.search-drawer__section');
    if (queriesSection) {
      queriesSection.after(skeleton);
    } else {
      inner.querySelector('[role="status"]')?.after(skeleton) || inner.prepend(skeleton);
    }
  }

  /**
   * Removes the skeleton, and restores the fallback products when the lookup found nothing to
   * replace them with.
   * @param {boolean} revealFallback - Whether to un-hide the fallback products section.
   */
  #clearSkuLoading(revealFallback) {
    const inner = this.refs.resultsContainer.querySelector('[data-search-results]');
    if (!inner) return;

    inner.querySelector('[data-sku-loading]')?.remove();

    if (!revealFallback) return;

    const fallback = inner.querySelector('[data-fallback-products]');
    if (fallback instanceof HTMLElement) fallback.hidden = false;
  }

  /**
   * Builds the loading placeholder: a heading plus three card-shaped blocks matching the
   * proportions of predictive-search-resource-carousel.liquid's slides, so the panel does not
   * resize when the real results replace it.
   * @param {string} [headingText] - The heading to sit above the placeholder cards.
   * @returns {HTMLElement}
   */
  #buildSkuSkeleton(headingText) {
    const section = document.createElement('div');
    section.className = 'search-drawer__section search-drawer__sku-loading';
    section.dataset.skuLoading = '';
    // Decorative: the live region already announces the real result count once it settles.
    section.setAttribute('aria-hidden', 'true');

    const header = document.createElement('div');
    header.className = 'predictive-search-results__resource-header';

    const heading = document.createElement('span');
    heading.className = 'predictive-search-results__title';
    heading.textContent = headingText || Theme.translations?.search_product_code_heading || 'Suggested Products';
    header.append(heading);

    const row = document.createElement('div');
    row.className = 'search-drawer__skeleton-row';

    for (let index = 0; index < 3; index++) {
      const card = document.createElement('div');
      card.className = 'search-drawer__skeleton-card';

      const media = document.createElement('div');
      media.className = 'search-drawer__skeleton-media';

      const line = document.createElement('div');
      line.className = 'search-drawer__skeleton-line';

      card.append(media, line);
      row.append(card);
    }

    section.append(header, row);
    return section;
  }

  /**
   * Whether the results panel currently shows genuine product matches — as opposed to a block's
   * `fallback_collection` substitutes (marked `data-fallback-products`) or nothing at all. Both
   * of the latter still leave room for a SKU match to be more relevant than what's showing.
   * @returns {boolean}
   */
  #hasRealProducts() {
    const card = this.refs.resultsContainer.querySelector('[data-resource-type="product"]');
    return card !== null && card.closest('[data-fallback-products]') === null;
  }

  /**
   * Shopify's predictive search API (/search/suggest.json) matches on product title and type
   * only — never variant SKU. The full search results page's backend does match SKU. So when
   * predictive search finds no real products for a query, this checks the results page and lifts
   * any genuine match into the drawer, letting a shopper who types a product code still find it.
   * @param {string} query - The term to check for a SKU/product-code match.
   * @param {AbortController} abortController - Aborted when a newer keystroke supersedes this one.
   * @returns {Promise<boolean>} Whether a genuine match was found and rendered.
   */
  async #trySkuFallback(query, abortController) {
    if (this.#hasRealProducts()) return false;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');

    let html;
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) return false;
      html = await response.text();
    } catch (error) {
      return false;
    }

    if (abortController.signal.aborted) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = Array.from(doc.querySelectorAll('#ResultsList .product-grid__item')).slice(0, 6);
    if (!items.length) return false;

    const cards = items
      .map((item) => {
        const link = item.querySelector('a[href]');
        const image = item.querySelector('img');
        if (!link) return null;

        const url = link.getAttribute('href') ?? '';

        return {
          url,
          // .text-block is where this store's current product-card configuration puts the
          // title; the handle is the fallback for any other configuration.
          title: item.querySelector('.text-block')?.textContent?.trim() || titleFromHandle(url) || query,
          imageSrc: image?.getAttribute('src') || '',
          imageSrcset: image?.getAttribute('srcset') || '',
          price: item.querySelector('.price')?.textContent.trim() || '',
        };
      })
      .filter(Boolean)
      .filter((card) => card.url !== '');

    if (!cards.length || abortController.signal.aborted) return false;

    this.#renderSkuFallback(cards);
    return true;
  }

  /**
   * Renders product-code matches found by #trySkuFallback into the results panel.
   *
   * Builds the same <slideshow-component>/<slideshow-slide> structure
   * predictive-search-resource-carousel.liquid renders server-side — not a simplified stand-in —
   * so a query that merely has a predictive-search gap (e.g. "christmas", zero real product
   * matches there despite matching plenty on the results page) still gets the real carousel:
   * working arrows from 4 items, keyboard nav, the lot. `on:click` is delegated globally
   * (assets/component.js) and Component#connectedCallback scans for `ref` attributes
   * synchronously, so a DOM subtree built by hand and then attached wires up identically to
   * server-rendered markup — verified live, not assumed.
   * @param {{url: string, title: string, imageSrc: string, imageSrcset: string, price: string}[]} cards
   */
  #renderSkuFallback(cards) {
    const inner = this.refs.resultsContainer.querySelector('[data-search-results]');
    if (!inner) return;

    inner.querySelector('[data-sku-fallback]')?.remove();

    // Removed here rather than left to #clearSkuLoading, which only runs a microtask later —
    // long enough to paint one frame with the skeleton and the real carousel stacked.
    inner.querySelector('[data-sku-loading]')?.remove();

    // A specific product-code match is always more relevant than the block's generic
    // fallback_collection substitutes — replace them rather than showing both under two
    // "Suggested Products" headings.
    const existingFallback = inner.querySelector('[data-fallback-products]');
    const headingText = existingFallback?.querySelector('.predictive-search-results__title')?.textContent?.trim();
    existingFallback?.remove();

    const section = document.createElement('div');
    section.className = 'search-drawer__section';
    section.dataset.skuFallback = '';

    const title = headingText || Theme.translations?.search_product_code_heading || 'Suggested Products';
    // Matches the >= 4 threshold in predictive-search-resource-carousel.liquid.
    const showArrows = cards.length >= 4;

    const slideshow = document.createElement('slideshow-component');
    slideshow.className = `predictive-search-results__list predictive-search-results__wrapper list-unstyled${
      showArrows ? '' : ' slideshow--single-media'
    }`;
    slideshow.style.setProperty('--slideshow-timeline', cards.map((_, i) => `--slide-${i + 1}`).join(', '));

    const header = document.createElement('div');
    header.className = 'predictive-search-results__resource-header';

    const heading = document.createElement('span');
    heading.className = 'predictive-search-results__title';
    heading.textContent = title;
    header.append(heading);

    if (showArrows) header.append(this.#buildSlideshowControls());

    const container = document.createElement('slideshow-container');
    container.setAttribute('ref', 'slideshowContainer');

    const slides = document.createElement('slideshow-slides');
    slides.setAttribute('tabindex', '-1');
    slides.setAttribute('ref', 'scroller');
    slides.setAttribute('gutters', 'start end');
    cards.forEach((card, index) => slides.append(this.#buildSlide(card, index)));

    container.append(slides);
    slideshow.append(header, container);
    section.append(slideshow);

    // A genuine match was found — "nothing matched your search term" would now be contradicted
    // by the product sitting right below it.
    inner.querySelector('[data-no-results]')?.remove();

    const queriesSection = inner.querySelector('.search-drawer__term-list')?.closest('.search-drawer__section');
    if (queriesSection) {
      queriesSection.after(section);
    } else {
      inner.querySelector('[role="status"]')?.after(section) || inner.prepend(section);
    }

    // slideshow-component's connectedCallback already ran, synchronously, the moment `section`
    // was attached above — same requirement as the morphed-in carousels in #getSearchResults.
    slideshow.classList.add('loaded');
  }

  /**
   * Builds the previous/next arrow pair, matching snippets/slideshow-controls.liquid +
   * snippets/slideshow-arrow.liquid for icon_style: 'chevron', shape: 'none'.
   * @returns {HTMLElement}
   */
  #buildSlideshowControls() {
    const CARET_SVG =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const controls = document.createElement('slideshow-controls');
    controls.setAttribute('ref', 'slideshowControls');
    controls.setAttribute('pagination-position', 'center');
    controls.setAttribute('scroll-mode', 'horizontal');

    /** @param {'previous' | 'next'} action */
    const buildArrow = (action) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'slideshow-controls__arrows';

      const button = document.createElement('button');
      button.type = 'button';
      button.className =
        `slideshow-control slideshow-control--${action} slideshow-control--shape-none ` +
        `slideshow-control--style-chevron button button-unstyled button-unstyled--transparent${
          action === 'previous' ? ' flip-x' : ''
        }`;
      button.setAttribute('aria-label', action === 'previous' ? 'Previous slide' : 'Next slide');
      button.setAttribute('on:click', `/${action}`);
      button.setAttribute('ref', action);
      button.innerHTML = `<span class="svg-wrapper icon-caret">${CARET_SVG}</span>`;

      wrapper.append(button);
      return wrapper;
    };

    controls.append(buildArrow('previous'), buildArrow('next'));
    return controls;
  }

  /**
   * Builds one slide, matching snippets/slideshow-slide.liquid + the resource-card markup
   * predictive-search-resource-carousel.liquid renders for resource_type: 'product'.
   * @param {{url: string, title: string, imageSrc: string, imageSrcset: string, price: string}} card
   * @param {number} index
   * @returns {HTMLElement}
   */
  #buildSlide(card, index) {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('view-timeline-name', `--slide-${index}`);
    slide.style.setProperty('--product-media-fit', 'cover');
    slide.className = 'predictive-search-results__card';

    const a = document.createElement('a');
    a.className = 'resource-card';
    a.href = card.url;
    a.dataset.resourceType = 'product';

    const media = document.createElement('div');
    media.className = 'resource-card__media';
    media.style.setProperty('--resource-card-aspect-ratio', '4 / 5');

    if (card.imageSrc) {
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.className = 'resource-card__image';
      img.src = card.imageSrc;
      if (card.imageSrcset) img.srcset = card.imageSrcset;
      img.alt = card.title;
      media.append(img);
    }

    const content = document.createElement('div');
    content.className = 'resource-card__content';

    const titleEl = document.createElement('h4');
    titleEl.className = 'resource-card__title paragraph';
    titleEl.textContent = card.title;
    content.append(titleEl);

    if (card.price) {
      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = card.price;
      content.append(price);
    }

    a.append(media, content);
    slide.append(a);
    return slide;
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { resultsContainer, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#bypassCorrectionFor = null;
    this.#hideResetButton();
    this.#createAbortController();

    resultsContainer.replaceChildren();
    this.dataset.searchState = 'empty';
    this.#resetScrollPositions();
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
